import type { FC, ReactElement, ReactNode } from "react"
import React, { useMemo } from "react"
import type { ComponentWithAs, IconProps, StackProps } from "@chakra-ui/react"
import {
  Button,
  Stack,
  Skeleton,
  Text,
  Flex,
  useClipboard,
  useDisclosure,
  Popover,
  PopoverTrigger,
  IconButton,
  PopoverContent,
  PopoverBody,
  Icon,
} from "@chakra-ui/react"

import type {
  Account,
  AccountId,
  BlockId,
  Maybe,
  ProposalEvent,
  ProposalStatusHistoryQuery,
} from "query/graphql"
import {
  ProposalStatus,
  useProposalStatusHistoryQuery,
  ProposalEventType,
} from "query/graphql"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import { chainIdToChainReference } from "web3/helpers/transformers"
import { Copy } from "ui/components/icons/font-awesome/Copy"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import { CircleInfo } from "ui/components/icons/font-awesome/CircleInfo"
import { Etherscan } from "ui/components/icons/custom/Etherscan"
import { Cube } from "ui/components/icons/font-awesome/Cube"
import { Plus } from "ui/components/icons/font-awesome/Plus"
import { Rocket } from "ui/components/icons/font-awesome/Rocket"
import { Play } from "ui/components/icons/font-awesome/Play"
import { Stop } from "ui/components/icons/font-awesome/Stop"
import { Hourglass } from "ui/components/icons/font-awesome/Hourglass"
import { Bolt } from "ui/components/icons/font-awesome/Bolt"
import { X } from "ui/components/icons/font-awesome/X"
import Timeline from "common/components/Timeline"
import { useToast } from "common/hooks/useToast"
import { TimelineState } from "common/types/timeline"
import { parseBlockId } from "web3/helpers/blockId"
import CardContainer from "common/components/CardContainer"
import Link from "common/components/Link"
import { getChainByChainId } from "web3/helpers/findChain"
import { Forward } from "ui/components/icons/font-awesome/Forward"
import { wasDefeatedByQuorum } from "proposal/helpers/votes"
import { ChainId } from "web3/constants/chains"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import { MessageCode } from "ui/components/icons/font-awesome/MessageCode"

type Props = {
  proposalId: string
  governorId: AccountId
  slug: string
}

const ProposalStatusHistory: FC<Props & StackProps> = ({
  proposalId,
  governorId,
  slug,
  ...stackProps
}) => {
  const { isLoading, data: section } = useProposalStatusHistoryQuery({
    input: {
      id: proposalId,
    },
  })

  const sortedSection = useMemo(() => {
    if (!section) return null

    const sortedEvents = section.proposal.events?.slice().sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    })

    return {
      ...section,
      proposal: {
        ...section.proposal,
        events: sortedEvents,
      },
    }
  }, [section])

  if (isLoading) {
    return (
      <CardContainer title="Status">
        <Stack as="section" spacing={4} {...stackProps}>
          <Stack border="gray.light" minH="sm" p={6} rounded="sm" spacing={10}>
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
          </Stack>
        </Stack>
      </CardContainer>
    )
  }

  if (!sortedSection) return null

  return (
    <CardContainer title="Status">
      <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} {...stackProps}>
        <Draft section={sortedSection} slug={slug} />
        <Publish section={sortedSection} slug={slug} />
        <Start section={sortedSection} />
        <Extended section={sortedSection} />
        <End section={sortedSection} />
        <Conclusion governorId={governorId} section={sortedSection} />
      </Stack>
    </CardContainer>
  )
}

const Conclusion: FC<{
  governorId: AccountId
  section: ProposalStatusHistoryQuery
}> = ({ section, governorId }) => {
  const status = section.proposal.status

  if (
    [
      ProposalStatus.Queued,
      ProposalStatus.Executed,
      ProposalStatus.Pendingexecution,
    ].some((type) => type === status)
  ) {
    return (
      <>
        <Queued governorId={governorId} section={section} />
        <Execute governorId={governorId} section={section} />
        <Message section={section} />
        <ExecuteL1 governorId={governorId} section={section} />
      </>
    )
  }

  if ([ProposalStatus.Canceled].some((type) => type === status)) {
    return <Canceled governorId={governorId} section={section} />
  }

  if ([ProposalStatus.Succeeded].some((type) => type === status)) {
    return <Succeeded section={section} />
  }

  if ([ProposalStatus.Defeated].some((type) => type === status)) {
    return <Defeated section={section} />
  }

  return (
    <>
      <Queued governorId={governorId} section={section} />
      <Execute governorId={governorId} section={section} />
      <Message section={section} />
      <ExecuteL1 governorId={governorId} section={section} />
    </>
  )
}

const Draft: FC<{
  slug: string
  section: ProposalStatusHistoryQuery
}> = ({ section, slug }) => {
  const didCreateDraft = Boolean(
    section.proposal.events?.find(
      (event) => event.type === ProposalEventType.Drafted,
    ),
  )

  if (!didCreateDraft) return null

  const proposer = section.proposal.proposer
  const state = section.proposal.events?.length
    ? TimelineState.Past
    : TimelineState.Active
  const label = state === TimelineState.Active ? "New draft" : "Draft created"

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Plus} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date timestamp={section.proposal.createdAt} />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        <Timeline.User
          account={proposer as Account}
          slug={slug}
          state={state}
        />
      </Flex>
    </Timeline.Container>
  )
}

const Publish: FC<{
  slug: string
  section: ProposalStatusHistoryQuery
}> = ({ slug, section }) => {
  const getState = (status?: ProposalStatus): TimelineState => {
    if (status === ProposalStatus.Pending) {
      return TimelineState.Active
    }

    return TimelineState.Past
  }

  const { creator, createdAt, block } = section.proposal
  const status = section.proposal.status
  const state = getState(status)
  const label =
    state === TimelineState.Disabled ? "Publish onchain" : "Published onchain"

  const txHash = useMemo(() => {
    const txHash = section.proposal.events?.find(
      (status) => status.type === ProposalEventType.Created,
    )?.txHash

    return txHash
  }, [section.proposal.events])

  const blockCreatedAt = useMemo(() => {
    const time = section.proposal.events?.find(
      (status) => status.type === ProposalEventType.Created,
    )?.block?.timestamp

    return time
  }, [section.proposal.events])

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Rocket} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date timestamp={blockCreatedAt ?? createdAt} />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        <Timeline.User account={creator as Account} slug={slug} state={state} />
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Start: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const getState = (status?: ProposalStatus) => {
    if (
      status === ProposalStatus.Active ||
      status === ProposalStatus.Extended
    ) {
      return TimelineState.Active
    }

    if (
      [
        ProposalStatus.Canceled,
        ProposalStatus.Defeated,
        ProposalStatus.Executed,
        ProposalStatus.Expired,
        ProposalStatus.Queued,
        ProposalStatus.Succeeded,
        ProposalStatus.Pendingexecution,
        ProposalStatus.Crosschainexecuted,
      ].some((type) => type === status)
    ) {
      return TimelineState.Past
    }

    return TimelineState.Disabled
  }

  const { proposal } = section
  const { start } = proposal
  const status = proposal.status

  const state = getState(status)
  const label =
    state === TimelineState.Disabled
      ? "Start voting period"
      : "Voting period started"

  const blockId = "id" in start ? start.id : undefined

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Play} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date timestamp={start.timestamp} />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        <Timeline.Estimation block={start} />
      </Flex>
      {blockId ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={blockId} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Extended: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const extendedEvent = section.proposal.events?.find(
    (event) => event.type === ProposalEventType.Extended,
  )

  if (!extendedEvent || !extendedEvent.block) {
    return null
  }
  const getState = (status?: ProposalStatus) => {
    if (
      status === ProposalStatus.Active ||
      status === ProposalStatus.Extended
    ) {
      return TimelineState.Active
    }

    if (
      [
        ProposalStatus.Canceled,
        ProposalStatus.Defeated,
        ProposalStatus.Executed,
        ProposalStatus.Expired,
        ProposalStatus.Queued,
        ProposalStatus.Succeeded,
        ProposalStatus.Pendingexecution,
      ].some((type) => type === status)
    ) {
      return TimelineState.Past
    }

    return TimelineState.Disabled
  }

  const { proposal } = section
  const status = proposal.status
  const state = getState(status)
  const label =
    state === TimelineState.Disabled
      ? "Extended voting period"
      : "Voting period extended"

  const blockId =
    "id" in extendedEvent.block ? extendedEvent.block.id : undefined

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Forward} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date timestamp={extendedEvent.block?.timestamp} />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        <Timeline.Estimation block={extendedEvent.block} />
      </Flex>
      {blockId ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={blockId} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const End: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const getState = (status?: ProposalStatus) => {
    if (
      [
        ProposalStatus.Canceled,
        ProposalStatus.Defeated,
        ProposalStatus.Executed,
        ProposalStatus.Crosschainexecuted,
        ProposalStatus.Expired,
        ProposalStatus.Queued,
        ProposalStatus.Succeeded,
        ProposalStatus.Pendingexecution,
      ].some((type) => type === status)
    ) {
      return TimelineState.Past
    }

    return TimelineState.Disabled
  }

  const { proposal } = section
  const { end } = proposal
  const status = proposal.status
  const state = getState(status)
  const label =
    state === TimelineState.Past ? "Voting period ended" : "End voting period"

  const blockId = "id" in end ? end.id : undefined

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Stop} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date timestamp={end.timestamp} />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        <Timeline.Estimation block={end} />
      </Flex>
      {blockId ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={blockId} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Queued: FC<{
  governorId: AccountId
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const { proposal } = section
  const { governor, events } = proposal
  const status = events?.[events.length - 1] as ProposalEvent
  const hasTimelock = Boolean(governor?.timelockId)

  if (!hasTimelock || !status) return null

  const getState = (event: ProposalEvent) => {
    const futureStatuses = [
      {
        type: ProposalEventType.Crosschainexecuted,
      },
      {
        type: ProposalEventType.Executed,
      },
      {
        type: ProposalEventType.Pendingexecution,
      },
    ]

    if (futureStatuses.some(({ type }) => type === event.type)) {
      return TimelineState.Past
    }

    if (event.type === ProposalEventType.Queued) {
      return TimelineState.Active
    }

    return TimelineState.Disabled
  }

  const state = getState(status)
  const queuedStatus = events?.find(
    (event) => event.type === ProposalEventType.Queued,
  )
  const transactionHash = queuedStatus?.txHash
  const label =
    state === TimelineState.Disabled ? "Queue proposal" : "Proposal queued"

  return (
    <Timeline.Container>
      <Timeline.Icon isShowingDecorativeLine as={Hourglass} state={state} />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Date
          timestamp={queuedStatus?.block?.timestamp ?? queuedStatus?.createdAt}
        />
        <Timeline.Title state={state}>{label}</Timeline.Title>
        {/* <Timeline.User account={account} governanceId={governanceId} state={state} /> */}
      </Flex>
      <MenuButton state={state}>
        <PopoverLink
          icon={CircleInfo}
          label="Learn more about queuing"
          route={EXTERNAL_ROUTES.tally.docs.queuingProposals()}
        />
        {queuedStatus?.block ? (
          <MenuBlockOptions
            blockId={queuedStatus.block.id}
            transactionHash={transactionHash}
          />
        ) : null}
      </MenuButton>
    </Timeline.Container>
  )
}

const Execute: FC<{
  governorId: AccountId
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const getState = (event?: ProposalEvent) => {
    if (event?.type === ProposalEventType.Executed) {
      return TimelineState.Active
    }

    if (event?.type === ProposalEventType.Crosschainexecuted) {
      return TimelineState.Past
    }

    return TimelineState.Disabled
  }

  const { isFeatureFlagOn } = useGovernanceContext()
  const isL1ExecutionEnabled = isFeatureFlagOn(FeatureFlag.L2Execution)

  const { proposal } = section

  // some UI only available for proposals created after this date
  const l1ExecutionAvailabilityDate = new Date(
    "Tue Jul 16 2024 10:55:31 GMT-0400 (Eastern Daylight Time)",
  )

  const createdEvent = proposal.events?.find(
    (event) => event.type === "created",
  )

  const showDecorativeLine = Boolean(
    isL1ExecutionEnabled &&
      createdEvent &&
      new Date(createdEvent.createdAt) > l1ExecutionAvailabilityDate,
  )

  const status = proposal.events?.[proposal.events.length - 1] as ProposalEvent
  const state = getState(status)
  const block = status?.block ?? undefined
  const txHash = status?.txHash ?? undefined
  const label = isL1ExecutionEnabled
    ? state === TimelineState.Disabled
      ? "L2 Execution"
      : "L2 Execution"
    : state === TimelineState.Disabled
    ? "Execute proposal"
    : "Proposal executed"

  return (
    <Timeline.Container>
      <Timeline.Icon
        as={Bolt}
        isShowingDecorativeLine={showDecorativeLine}
        state={state}
      />
      <Flex direction="column" flexGrow={1}>
        {block ? (
          <Timeline.Date
            shouldRender={state === TimelineState.Active}
            timestamp={block.timestamp}
          />
        ) : null}
        <Timeline.Title state={state}>{label}</Timeline.Title>
        {isL1ExecutionEnabled && state === TimelineState.Disabled ? (
          <Timeline.Estimation customText="Available 8 days after queue" />
        ) : null}
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <PopoverLink
            icon={CircleInfo}
            label="Learn more about executing"
            route={EXTERNAL_ROUTES.tally.docs.executingProposals()}
          />
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Message: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const { isFeatureFlagOn } = useGovernanceContext()
  const isL1ExecutionEnabled = isFeatureFlagOn(FeatureFlag.L2Execution)

  if (!isL1ExecutionEnabled) return null

  const getState = (event?: ProposalEvent) => {
    if (event?.type === ProposalEventType.Crosschainexecuted) {
      return TimelineState.Past
    }

    return TimelineState.Disabled
  }

  const { proposal } = section

  const l1Events = proposal.events?.filter(
    (event) => event.chainId === ChainId.EthereumMainnet,
  )
  const status = l1Events?.[l1Events.length - 1] as ProposalEvent
  const state = getState(status)

  // some UI only available for proposals created after this date
  const l1ExecutionAvailabilityDate = new Date(
    "Tue Jul 16 2024 10:55:31 GMT-0400 (Eastern Daylight Time)",
  )

  const createdEvent = proposal.events?.find(
    (event) => event.type === "created",
  )

  const showDecorativeLine = Boolean(
    isL1ExecutionEnabled &&
      createdEvent &&
      new Date(createdEvent.createdAt) > l1ExecutionAvailabilityDate,
  )

  if (
    !createdEvent ||
    new Date(createdEvent.createdAt) <= l1ExecutionAvailabilityDate
  ) {
    return null
  }

  return (
    <Timeline.Container>
      <Timeline.Icon
        as={MessageCode}
        isShowingDecorativeLine={showDecorativeLine}
        state={state}
      />
      <Flex direction="column" flexGrow={1}>
        <Timeline.Title state={state}>L2-to-L1 message</Timeline.Title>
        {isL1ExecutionEnabled ? (
          <Timeline.Estimation customText="~1 week" />
        ) : null}
        {}
      </Flex>

      <MenuButton state={state}>
        <PopoverLink
          icon={CircleInfo}
          label="Learn more about executing"
          route={EXTERNAL_ROUTES.tally.docs.executingProposals()}
        />
      </MenuButton>
    </Timeline.Container>
  )
}

const ExecuteL1: FC<{
  governorId: AccountId
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const { isFeatureFlagOn } = useGovernanceContext()
  const isL1ExecutionEnabled = isFeatureFlagOn(FeatureFlag.L2Execution)

  if (!isL1ExecutionEnabled) return null

  const getState = (event?: ProposalEvent) => {
    if (event?.type === ProposalEventType.Crosschainexecuted) {
      return TimelineState.Active
    }

    return TimelineState.Disabled
  }

  const { proposal } = section

  // some UI only available for proposals created after this date
  const l1ExecutionAvailabilityDate = new Date(
    "Tue Jul 16 2024 10:55:31 GMT-0400 (Eastern Daylight Time)",
  )

  const createdEvent = proposal.events?.find(
    (event) => event.type === "created",
  )

  if (
    !createdEvent ||
    new Date(createdEvent.createdAt) <= l1ExecutionAvailabilityDate
  ) {
    return null
  }
  const l1Events = proposal.events?.filter(
    (event) => event.chainId === ChainId.EthereumMainnet,
  )
  const status = l1Events?.[l1Events.length - 1] as ProposalEvent
  const state = getState(status)
  const block = status?.block ?? undefined
  const txHash = status?.txHash ?? undefined
  const label =
    state === TimelineState.Disabled ? "L1 Execution" : "L1 Execution"

  return (
    <Timeline.Container>
      <Timeline.Icon as={Bolt} state={state} />
      <Flex direction="column" flexGrow={1}>
        {block ? (
          <Timeline.Date
            shouldRender={state === TimelineState.Active}
            timestamp={block.timestamp}
          />
        ) : null}
        <Timeline.Title state={state}>{label}</Timeline.Title>
        {isL1ExecutionEnabled && state === TimelineState.Disabled ? (
          <Timeline.Estimation customText="Available 3 days after message" />
        ) : null}
        {}
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <PopoverLink
            icon={CircleInfo}
            label="Learn more about executing"
            route={EXTERNAL_ROUTES.tally.docs.executingProposals()}
          />
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Defeated: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const getLabel = (section: ProposalStatusHistoryQuery) => {
    const { proposal } = section

    if (proposal.quorum && proposal.governor) {
      return wasDefeatedByQuorum(
        proposal.governor,
        proposal.quorum,
        proposal.governor.token.decimals,
        proposal.voteStats,
      )
        ? "Quorum not reached"
        : "Proposal defeated"
    }

    return "Proposal defeated"
  }

  const { proposal } = section
  const status = proposal.events?.[proposal.events.length - 1]
  const state = TimelineState.Active
  const block = status?.block ?? undefined
  const txHash = status?.txHash ?? undefined
  const label = getLabel(section)

  return (
    <Timeline.Container>
      <Timeline.Icon as={X} state={state} />
      <Flex direction="column" flexGrow={1}>
        {block ? (
          <Timeline.Date
            shouldRender={state === TimelineState.Active}
            timestamp={block.timestamp}
          />
        ) : null}
        <Timeline.Title state={state}>{label}</Timeline.Title>
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Succeeded: FC<{
  section: ProposalStatusHistoryQuery
}> = ({ section }) => {
  const { proposal } = section
  const status = proposal.events?.[proposal.events.length - 1]
  const state = TimelineState.Active
  const block = status?.block ?? undefined
  const txHash = status?.txHash ?? undefined

  return (
    <Timeline.Container>
      <Timeline.Icon as={Bolt} state={state} />
      <Flex direction="column" flexGrow={1}>
        {block ? (
          <Timeline.Date
            shouldRender={state === TimelineState.Active}
            timestamp={block.timestamp}
          />
        ) : null}
        <Timeline.Title state={state}>Proposal succeeded</Timeline.Title>
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const Canceled: FC<{
  governorId: AccountId
  section: ProposalStatusHistoryQuery
  // }> = ({ governanceId, section }) => {
}> = ({ section }) => {
  const { proposal } = section
  const status = proposal.events?.[proposal.events.length - 1]
  const state = TimelineState.Active
  const block = status?.block ?? undefined
  const txHash = status?.txHash ?? undefined

  return (
    <Timeline.Container>
      <Timeline.Icon as={X} state={state} />
      <Flex direction="column" flexGrow={1}>
        {block ? (
          <Timeline.Date
            shouldRender={state === TimelineState.Active}
            timestamp={block.timestamp}
          />
        ) : null}
        <Timeline.Title state={state}>Proposal canceled</Timeline.Title>
      </Flex>
      {block ? (
        <MenuButton state={state}>
          <PopoverLink
            icon={CircleInfo}
            label="Learn more about cancelling"
            route={EXTERNAL_ROUTES.tally.docs.cancelingProposals()}
          />
          <MenuBlockOptions blockId={block.id} transactionHash={txHash} />
        </MenuButton>
      ) : null}
    </Timeline.Container>
  )
}

const MenuButton: FC<{ state: TimelineState; children: ReactNode }> = ({
  state,
  children,
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure()

  if (state === TimelineState.Disabled) return null

  return (
    <Popover
      isOpen={isOpen}
      placement="bottom-start"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>
        <IconButton
          aria-label="Open publish step options"
          bg="white"
          h={8}
          icon={<Icon as={EllipsisVertical} fill="gray.400" h={4} w={4} />}
          minW={8}
          size="sm"
        />
      </PopoverTrigger>
      <PopoverContent w="auto">
        <PopoverBody>{children}</PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

const MenuBlockOptions: FC<{
  blockId: BlockId
  transactionHash?: Maybe<string>
}> = ({ blockId, transactionHash }) => {
  const { chainId, number } = parseBlockId(blockId)
  const { handleCopy } = useCopy(number)
  const chainReference = chainIdToChainReference(chainId)
  const foundChain = getChainByChainId(chainId)
  const networkName = foundChain ? foundChain.mediumName : "unknown"

  return (
    <Flex align="stretch" flexDirection="column">
      {transactionHash ? (
        <PopoverLink
          isExternal
          icon={Etherscan}
          label="View on Block Explorer"
          route={EXTERNAL_ROUTES.etherscan.hash(
            chainReference,
            transactionHash,
          )}
        />
      ) : null}
      <PopoverButton
        icon={Cube}
        label={
          <Stack align="center" direction="row" spacing={2}>
            <Text textStyle="body.bold.md">{`${networkName} block: ${number}`}</Text>
            <Icon as={Copy} color="gray.400" />
          </Stack>
        }
        onClick={handleCopy}
      />
    </Flex>
  )
}

const useCopy = (blockNumber: number): { handleCopy: () => void } => {
  const { onCopy } = useClipboard(String(blockNumber))
  const { toast } = useToast()

  const handleCopy = (): void => {
    onCopy()

    toast({
      status: "success",
      title: `Block number copied`,
    })
  }

  return { handleCopy }
}

export default ProposalStatusHistory

function PopoverLink({
  icon,
  label,
  isExternal,
  route,
}: {
  route: string
  icon: ComponentWithAs<"svg", IconProps>
  label: string | ReactElement
  isExternal?: boolean
}) {
  return (
    <Link
      className="no-underline"
      href={route}
      isExternal={isExternal}
      w="full"
    >
      <Button
        _focusVisible={{
          boxShadow: "none",
          borderColor: "transparent",
          outline: "3px solid hsla(216, 12%, 84%, 1)",
          outlineOffset: "2px",
        }}
        _hover={{
          bg: "gray.100",
        }}
        bg="white"
        border="transparent.1"
        mb={2.5}
        w="full"
      >
        <Stack isInline align="center" spacing={2} w="full">
          <Icon as={icon} h={5} w={5} />
          <Text textStyle="body.bold.md">{label}</Text>
        </Stack>
      </Button>
    </Link>
  )
}

function PopoverButton({
  icon,
  label,
  onClick,
}: {
  icon: ComponentWithAs<"svg", IconProps>
  label: ReactElement
  onClick: () => void
}) {
  return (
    <Button
      _focusVisible={{
        boxShadow: "none",
        borderColor: "transparent",
        outline: "3px solid hsla(216, 12%, 84%, 1)",
        outlineOffset: "2px",
      }}
      _hover={{
        bg: "gray.100",
      }}
      bg="white"
      border="transparent.1"
      w="full"
      onClick={onClick}
    >
      <Stack isInline align="center" spacing={2} w="full">
        <Icon as={icon} h={5} w={5} />
        {label}
      </Stack>
    </Button>
  )
}
